import { hasOwnProperty } from "@glideapps/ts-necessities";
import { DateFormat, formatDateTime, TimeFormat } from "../../format-date-time";
import { checkString } from "../../Support";
import { AppKind } from "../app-kind";
import { localizedStringsGlobalInstance } from "./base";
import type { DevStringKey as InternalDevStringKey } from "./dev";
import { getDevLocalizedString, intentionallyUntranslatedLocalizedDevStrings, localizedDevStrings } from "./dev";
import { englishLocalizedStrings } from "./english";
import type { LocalizedStringKey as InternalLocalizedStringKey } from "./types";

export { initializeLocalization } from "./browser";
export {
    formatDevLocalizedString,
    getDevLocalizedString,
    haveUnintentionallyUntranslatedStrings,
    intentionallyUntranslatedLocalizedDevStrings,
} from "./dev";

type LocalizationDomain = AppKind | "builder";

function shouldTranslate(domain: LocalizationDomain): boolean {
    return domain === AppKind.App;
}

// We have "properly" configured our TypeScript compiler to not re-export types.
// That's too bad TypeScript, I'm re-exporting these types anyway.
export type LocalizedStringKey = InternalLocalizedStringKey;
export type DevStringKey = InternalDevStringKey;

export function getLocalizedString(name: LocalizedStringKey, domain: LocalizationDomain): string {
    if (!shouldTranslate(domain)) {
        return englishLocalizedStrings[name];
    }

    const localizedStrings = localizedStringsGlobalInstance();
    return localizedStrings.getString(name, localizedStrings.getLanguage(), false);
}

export function getAnyLocalizedString(name: LocalizedStringKey | DevStringKey, domain: LocalizationDomain): string {
    if (englishLocalizedStrings[name as LocalizedStringKey] !== undefined) {
        return getLocalizedString(name as LocalizedStringKey, domain);
    }
    if (
        hasOwnProperty(localizedDevStrings, name) ||
        hasOwnProperty(intentionallyUntranslatedLocalizedDevStrings, name)
    ) {
        return getDevLocalizedString(name as DevStringKey);
    }
    return name;
}

export function formatLocalizedString(
    name: LocalizedStringKey,
    strings: ReadonlyArray<string>,
    domain: LocalizationDomain = AppKind.App
): string {
    if (!shouldTranslate(domain)) {
        const lang = localizedStringsGlobalInstance().getLanguage();
        try {
            localizedStringsGlobalInstance().setLanguage("en");
            const result = checkString(localizedStringsGlobalInstance().formatString(name, ...strings));
            return result;
        } finally {
            localizedStringsGlobalInstance().setLanguage(lang);
        }
    }

    return checkString(localizedStringsGlobalInstance().formatString(name, ...strings));
}

// e.g. 8:30 AM
export function formatShortTime(d: Date): string | undefined {
    return formatDateTime(d, undefined, TimeFormat.WithoutSeconds);
}

// e.g. Wed, Jul 17 2019
export function formatShortDate(d: Date, showDayOfWeek: boolean = true): string | undefined {
    return formatDateTime(d, showDayOfWeek ? DateFormat.Long : DateFormat.Medium, undefined);
}

export function getDateString(
    domain: LocalizationDomain,
    date: Date,
    now: Date,
    startOfDay: Date,
    forBuilder: boolean,
    nowMintues?: number
): string | undefined {
    let difference = now.getTime() - date.getTime();
    difference = Math.floor(difference / (1000 * 60)); // move to minutes

    if (difference < (nowMintues ?? 3)) {
        return forBuilder ? "Just now" : getLocalizedString("justNow", domain);
    } else if (difference < 60) {
        return forBuilder
            ? `${difference.toString()} minutes ago`
            : formatLocalizedString("nMinutes", [difference.toString()]);
    }

    const startDay = startOfDay.getTime();
    const isToday = date.getTime() > startDay;
    const isYesterday = !isToday && date.getTime() > startDay - 1000 * 60 * 60 * 24;

    difference = Math.floor(difference / 60); // move to hours
    if (difference === 1) {
        return forBuilder ? `One hour ago` : getLocalizedString("oneHour", domain);
    } else if (difference < 12) {
        return forBuilder
            ? `${difference.toString()} hours ago`
            : formatLocalizedString("nHours", [difference.toString()]);
    } else if (isToday) {
        return forBuilder ? `Today` : getLocalizedString("today", domain);
    } else if (isYesterday) {
        return forBuilder ? `Yesterday` : getLocalizedString("yesterday", domain);
    } else if (difference < 168) {
        return formatDateTime(date, DateFormat.WeekdayOnly, undefined);
    } else {
        return formatShortDate(date, false);
    }
}
